import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, CaretUpDown, Plus } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";

// Combobox homologado alimentado desde /hr/catalog/{kind}.
// Permite elegir de la lista precargada o agregar un nuevo valor al vuelo.
const _cache = {};
export function _resetHrCatalogCache(kind) {
  if (kind) delete _cache[kind];
  else Object.keys(_cache).forEach((k) => delete _cache[k]);
}

export function CatalogCombobox({ kind, value, onChange, placeholder = "Seleccionar…", canManage = true, testid }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(_cache[kind] || []);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/hr/catalog/${kind}`)
      .then((r) => { _cache[kind] = r.data || []; setItems(r.data || []); })
      .catch(() => {});

  useEffect(() => {
    if (open && !_cache[kind]) load();
    else if (open) setItems(_cache[kind] || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const q = query.trim();
  const exists = items.some((i) => i.name.toLowerCase() === q.toLowerCase());

  const addNew = async () => {
    if (!q || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/hr/catalog/${kind}`, { name: q });
      const next = [...items.filter((x) => x.name.toLowerCase() !== r.data.name.toLowerCase()), r.data]
        .sort((a, b) => a.name.localeCompare(b.name));
      _cache[kind] = next;
      setItems(next);
      onChange(r.data.name);
      setQuery("");
      setOpen(false);
      toast.success("Agregado al catálogo");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" data-testid={testid}
          className="w-full justify-between font-normal h-10 px-3">
          <span className={cn("truncate", !value && "text-slate-400")}>{value || placeholder}</span>
          <CaretUpDown size={14} className="opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
        <Command shouldFilter>
          <CommandInput placeholder="Buscar o escribir…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty className="py-2">
              {canManage && q ? (
                <button type="button" onClick={addNew} disabled={busy}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-blue-900 hover:bg-blue-50"
                  data-testid={`${testid}-add-empty`}>
                  <Plus size={14} /> Agregar «{q}»
                </button>
              ) : <span className="text-slate-400 text-sm">Sin resultados</span>}
            </CommandEmpty>
            <CommandGroup>
              {items.map((i) => (
                <CommandItem key={i.id} value={i.name}
                  onSelect={() => { onChange(i.name); setOpen(false); setQuery(""); }}
                  data-testid={`${testid}-opt-${i.id}`}>
                  <Check size={14} className={cn("mr-1", value === i.name ? "opacity-100" : "opacity-0")} />
                  {i.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {canManage && q && !exists && (
              <div className="border-t">
                <button type="button" onClick={addNew} disabled={busy}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-900 hover:bg-blue-50"
                  data-testid={`${testid}-add`}>
                  <Plus size={14} /> Agregar «{q}» al catálogo
                </button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
